import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Flex,
  VStack,
  HStack,
  Text,
  Image,
  Badge,
  Button,
  Input,
  InputGroup,
  InputLeftElement,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Avatar,
  IconButton,
  Tabs,
  TabList,
  Tab,
  Grid,
  GridItem,
  Select,
} from "@chakra-ui/react";
import {
  ArrowLeft,
  Search,
  Filter,
  MoreVertical,
  MapPin,
  Phone,
  Mail,
  Calendar,
  Droplet,
  Users,
  User,
} from "lucide-react";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { DoctorOverview } from "@/types";
import { userService } from "@/lib/services/userService";
import { isoToTime, toAmPm } from "@/util/convertTime";
import { appointmentService } from "@/lib/services/appointmentService";
import { DataTable, DataTableOptions, DataTableRef } from "@/components/tables";
import { useAccessCheck } from "@/hooks/useAccess";

const DoctorProfilePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const access = useAccessCheck();

  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState(0);
  const [availabilityFilter, setAvailabilityFilter] = useState("Monday");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [doctor, setDoctor] = useState<DoctorOverview | null>(null);
  const [availability, setAvailability] = useState<{
    [key: string]: { start: string; end: string }[];
  }>({});
  const [appointments, setAppointments] = useState<any[]>([]);

  useEffect(() => {
    const fetchDoctor = async () => {
      if (!id) {
        return setDoctor(null);
      }
      try {
        const doc = await userService.getDoctor(id);
        setDoctor(doc);
      } catch (error) {
        console.error('Failed to fetch doctor:', error);
      }
    }
    
    fetchDoctor();
  }, [id]);

  useEffect(() => {
    if (!doctor?.availabilities) return;

    const formatAvailability = (availabilityData: any[]) => {
      const grouped: { [key: string]: { start: string; end: string }[] } = {};

      availabilityData.forEach((slot) => {
        const day =
          slot.session_day.charAt(0).toUpperCase() + slot.session_day.slice(1);

        if (!grouped[day]) {
          grouped[day] = [];
        }

        grouped[day].push({
          start: toAmPm(slot.start_time),
          end: toAmPm(slot.end_time),
        });
      });

      return grouped;
    };
    console.log("Doctor Availabilities:", doctor.availabilities);
    setAvailability(formatAvailability(doctor.availabilities));
  }, [doctor]);

   const tableOptions: DataTableOptions = useMemo(
    () => ({
      dataFields: {
        appointment_datetime: {
          name: "Date & Time",
          maxWidth: "200px",
        },
        patient_html: {
          name: "Patient",
          maxWidth: "200px",
        },
        procedures_text: {
          name: "Service",
          maxWidth: "150px",
          className: "truncate",
        },
        status: {
          name: "Status",
          maxWidth: "200px",
          badge: {
            "1": "green",
            "0": "yellow",
          },
          badgeLabels: {
            "1": "Active",
            "0": "Inactive",
          },
        },
      },
      dataSource: `/appointments/user/${id}`,
      primaryKey: "id",
      softDelete: true,
      displayInModal: true,
      noRecordText: "No appointments found",
    }),
    [],
  );

  const permissions = useMemo(
    () => ({
      can_edit: access.can_edit,
      can_delete: access.can_delete,
      can_create: access.can_create,
      can_view: access.can_view,
      can_export: access.can_export,
      can_import: access.can_import,
    }),
    [access],
  );

  if (!doctor) {
    return (
      <AdminLayout>
        <Box minH="100vh" bg="#FEFCF8" p={{ base: 3, md: 5 }}>
          <Text>Loading...</Text>
        </Box>
      </AdminLayout>
    );
  }

  const imageUrl = doctor.image?.file_url || "/assets/img/profile.jpg";

  return (
    <AdminLayout>
      <Box minH="100vh" bg="#FEFCF8" p={{ base: 3, md: 5 }}>
        <VStack spacing={4} align="stretch">
          <HStack mb={2}>
            <Button
              leftIcon={<ArrowLeft size={16} />}
              variant="ghost"
              size="sm"
              onClick={() => navigate(-1)}
            >
              Go Back
            </Button>
          </HStack>

          <Grid templateColumns={{ base: "1fr", lg: "2fr 1fr" }} gap={4}>
            <GridItem>
              <VStack spacing={4}>
                <Box
                  bgImage="url('/assets/img/viewdoctorbg.png')"
                  bgSize="cover"
                  bgPosition="center"
                  bgRepeat="no-repeat"
                  p={{ base: 3, sm: 4, md: 6 }}
                  mb={4}
                  position="relative"
                  width="100%"
                  height={{ base: "auto", md: "auto" }}
                  rounded={{ base: "md", md: "lg" }}
                  overflow="hidden"
                >
                  <Flex gap={{ base: 4, md: 6 }} direction={{ base: "column", sm: "row" }}>
                    <Image 
                      src={imageUrl} 
                      alt={doctor.user_fullname} 
                      boxSize={{ base: "100px", md: "120px" }} 
                      borderRadius="lg" 
                      objectFit="cover" 
                      border="2px solid" 
                      borderColor="gray.100"
                      fallbackSrc="/assets/img/profile.jpg"
                    />

                    <Flex
                      flex="1"
                      justify="space-between"
                      direction={{ base: "column", lg: "row" }}
                      gap={4}
                    >
                      <VStack align="start" spacing={2}>
                        <Text
                          fontSize={{ base: "xl", md: "2xl" }}
                          fontWeight="bold"
                        >
                          {doctor.user_fullname}
                        </Text>
                        <HStack flexWrap="wrap">
                          <Badge colorScheme="blue" fontSize="xs">
                            {doctor.department_id?.department || "N/A"}
                          </Badge>
                          <Badge colorScheme="green" fontSize="xs">
                            {doctor.user_status == 1 ? "Active" : "Inactive"}
                          </Badge>
                        </HStack>
                        <Text fontSize="sm" color="gray.600">
                          {doctor.designation_id?.designation || "N/A"}
                        </Text>
                        <HStack fontSize="sm" color="gray.600">
                          <MapPin size={14} />
                          <Text>Clinic : {doctor.branch?.name || "N/A"}</Text>
                        </HStack>
                      </VStack>

                      <VStack
                        align={{ base: "start", lg: "end" }}
                        spacing={2}
                        p={{ base: 3, md: 4 }}
                        borderRadius="md"
                      >
                        <Text fontSize="sm" fontWeight="semibold">
                          Consultation Charge
                        </Text>
                        <Text
                          fontSize={{ base: "md", md: "lg" }}
                          fontWeight="bold"
                        >
                          ₱ {doctor.consultation_fee || "0.00"} / Physical
                        </Text>
                        <Button
                          colorScheme="red"
                          size="sm"
                          mt={2}
                          w={{ base: "full", lg: "auto" }}
                        >
                          Book Appointment
                        </Button>
                      </VStack>
                    </Flex>
                  </Flex>
                </Box>

                <Box
                  bg="white"
                  borderRadius="lg"
                  border="1px solid"
                  borderColor="gray.200"
                  p={{ base: 4, md: 6 }}
                  w="full"
                  shadow="sm"
                >
                  <VStack align="stretch" spacing={4}>
                    <Box pb={4} borderBottom="1px solid" borderColor="gray.200">
                      <Flex
                        justify="space-between"
                        align={{ base: "start", md: "center" }}
                        direction={{ base: "column", md: "row" }}
                        gap={3}
                        mb={4}
                      >
                        <Text fontSize="lg" fontWeight="bold">
                          Availability
                        </Text>
                      </Flex>
                      <Tabs variant="unstyled" size="sm">
                        <TabList
                          borderBottom="1px solid"
                          borderColor="gray.200"
                          overflowX="auto"
                          css={{
                            "&::-webkit-scrollbar": { display: "none" },
                            scrollbarWidth: "none",
                          }}
                        >
                          {Object.keys(availability).map((day) => (
                            <Tab
                              key={day}
                              fontSize={{ base: "xs", md: "sm" }}
                              px={{ base: 2, md: 3 }}
                              _selected={{
                                borderBottom: "2px solid",
                                borderColor: "blue.500",
                                fontWeight: "bold",
                                color: "blue.500",
                              }}
                              onClick={() => setAvailabilityFilter(day)}
                            >
                              {day.substring(0, 3)}
                            </Tab>
                          ))}
                        </TabList>
                      </Tabs>

                      <Grid templateColumns="repeat(5, 1fr)" gap={4}>
                        {Object.entries(availability)
                          .filter(([day]) => day === availabilityFilter)
                          .map(([day, slots]) => (
                            <VStack
                              key={day}
                              align="stretch"
                              spacing={2}
                              marginTop={4}
                            >
                              {slots.map((slot, idx) => (
                                <Box
                                  key={idx}
                                  bg="gray.50"
                                  p={2}
                                  borderRadius="md"
                                  textAlign="center"
                                  fontSize="xs"
                                >
                                  <Text>
                                    {slot.start} - {slot.end}
                                  </Text>
                                </Box>
                              ))}
                            </VStack>
                          ))}
                      </Grid>
                    </Box>
                    <Tabs variant="unstyled" onChange={setActiveTab}>
                      <TabList borderBottom="1px solid" borderColor="gray.200">
                        <Tab
                          fontSize={{ base: "sm", md: "md" }}
                          _selected={{
                            borderBottom: "2px solid",
                            borderColor: "blue.500",
                            fontWeight: "bold",
                            color: "blue.500",
                          }}
                        >
                          Appointments
                        </Tab>
                        <Tab
                          fontSize={{ base: "sm", md: "md" }}
                          _selected={{
                            borderBottom: "2px solid",
                            borderColor: "blue.500",
                            fontWeight: "bold",
                            color: "blue.500",
                          }}
                        >
                          Transactions
                        </Tab>
                      </TabList>

                      <Box mt={4}>
                        <Flex
                          justify="space-between"
                          mb={4}
                          direction={{ base: "column", md: "row" }}
                          gap={3}
                        >
                          <InputGroup
                            maxW={{ base: "full", md: "250px" }}
                            size="sm"
                          >
                            <InputLeftElement pointerEvents="none">
                              <Search size={16} color="gray" />
                            </InputLeftElement>
                            <Input
                              placeholder="Search"
                              value={searchTerm}
                              onChange={(e) => setSearchTerm(e.target.value)}
                              border="1px solid"
                              borderColor="gray.200"
                            />
                          </InputGroup>
                        </Flex>

                        {activeTab === 0 ? (
                          <>
                          <DataTable
                            options={tableOptions}
                            access={permissions}
                            itemClickable={true}
                            onOpenItem={(id) =>
                              navigate(`/appointments/view-appointment/${id}`)
                            }
                            showCheckbox={false}
                            editable={false}
                            hideActions={true}
                          />
                          </>
                        ) : (
                          <>
                            <Box
                              overflowX="auto"
                              border="1px solid"
                              borderColor="gray.200"
                              borderRadius="md"
                            >
                              <Table variant="simple" size="sm">
                                <Thead bg="gray.50">
                                  <Tr>
                                    <Th fontSize="xs">Transaction ID</Th>
                                    <Th fontSize="xs">Date</Th>
                                    <Th
                                      fontSize="xs"
                                      display={{
                                        base: "none",
                                        md: "table-cell",
                                      }}
                                    >
                                      Amount
                                    </Th>
                                    <Th fontSize="xs">Status</Th>
                                    <Th></Th>
                                  </Tr>
                                </Thead>
                                <Tbody>
                                  <Tr>
                                    <Td colSpan={5} textAlign="center" py={8}>
                                      <Text color="gray.500">
                                        No transactions available
                                      </Text>
                                    </Td>
                                  </Tr>
                                </Tbody>
                              </Table>
                            </Box>
                          </>
                        )}
                      </Box>
                    </Tabs>
                  </VStack>
                </Box>
              </VStack>
            </GridItem>
            <GridItem>
              <Box
                bg="white"
                borderRadius="lg"
                border="1px solid"
                borderColor="gray.200"
                p={{ base: 4, md: 6 }}
                shadow="sm"
              >
                <Text fontSize="lg" fontWeight="bold" mb={4}>
                  About
                </Text>
                <VStack align="stretch" spacing={3}>
                  {[
                    { icon: Calendar, label: "Medical license Number", value: doctor.medical_license_number || "N/A" },
                    { icon: Phone, label: "Phone Number", value: doctor.user_phone_number || "N/A" },
                    { icon: Mail, label: "Email Address", value: doctor.user_email || "N/A" },
                    { icon: MapPin, label: "Location", value: doctor.address1?.city != null ? `${doctor.address1.city}, ${doctor.address1.state}, ${doctor.address1.country}` : "N/A" },
                    { icon: Calendar, label: "DOB", value: doctor.birthday || "N/A" },
                    { icon: Droplet, label: "Blood Group", value: doctor.blood_group || "N/A" },
                    { icon: Users, label: "Year of Experience", value: doctor.years_of_experience || "N/A" },
                    { icon: User, label: "Gender", value: doctor.gender?.toUpperCase() || "N/A" },
                  ].map((item, index) => {
                    const Icon = item.icon;
                    return (
                      <HStack
                        key={index}
                        px={3}
                        py={2}
                        borderRadius="md"
                        _hover={{ bg: "gray.100" }}
                        transition="all 0.2s"
                      >
                        <Box bg="white" p={2} borderRadius="md">
                          <Icon size={18} color="#4A5568" />
                        </Box>
                        <VStack align="start" spacing={0} flex="1">
                          <Text fontSize="xs" color="gray.500">
                            {item.label}
                          </Text>
                          <Text fontSize="sm" fontWeight="medium" noOfLines={2}>
                            {item.value}
                          </Text>
                        </VStack>
                      </HStack>
                    );
                  })}
                </VStack>
              </Box>
            </GridItem>
          </Grid>
        </VStack>
      </Box>
    </AdminLayout>
  );
};

export default DoctorProfilePage;
